<template>
  <div class="app-shell" :class="{ 'sidebar-open': sidebarOpen }">
    <!-- overlay (mobile only) -->
    <div class="overlay" @click="sidebarOpen = false"></div>

    <aside class="sidebar border-end">
      <div class="sidebar-header d-flex align-items-center gap-2 px-3">
        <div class="brand-icon">
          <i class="bi bi-graph-up"></i>
        </div>
        <div class="flex-grow-1">
          <div class="brand-title">GVSG Savings</div>
          <div class="brand-subtitle">Admin Panel</div>
        </div>

        <button
          class="btn btn-sm btn-outline-secondary d-lg-none"
          @click="sidebarOpen = false"
          aria-label="Close menu"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <nav class="nav flex-column px-2 mt-2">
        <template v-for="(sec, i) in filteredMenu" :key="i">
          <div
            v-if="sec.section"
            class="px-2 mt-3 mb-2 text-uppercase small text-muted fw-semibold"
          >
            {{ sec.section }}
          </div>

          <template v-for="item in sec.items" :key="itemKey(item)">
            <!-- Regular single link -->
            <RouterLink
              v-if="!item.children || !item.children.length"
              :to="item.to"
              class="nav-link"
              active-class="active"
              exact-active-class="active"
            >
              <i :class="`bi ${item.icon} me-2`"></i>
              <span>{{ item.label }}</span>
            </RouterLink>

            <!-- Parent item with submenu -->
            <div v-else class="nav-group">
              <button
                type="button"
                class="nav-link nav-link-group w-100 border-0 bg-transparent"
                :class="{
                  active: isParentActive(item),
                  open: isOpen(itemKey(item)),
                }"
                @click="toggleGroup(itemKey(item))"
              >
                <span class="d-flex align-items-center">
                  <i :class="`bi ${item.icon} me-2`"></i>
                  <span>{{ item.label }}</span>
                </span>

                <i
                  class="bi ms-2 submenu-chevron"
                  :class="
                    isOpen(itemKey(item)) ? 'bi-chevron-up' : 'bi-chevron-down'
                  "
                ></i>
              </button>

              <div v-show="isOpen(itemKey(item))" class="submenu">
                <RouterLink
                  v-for="child in item.children"
                  :key="child.to"
                  :to="child.to"
                  class="nav-link nav-sublink"
                  active-class="active"
                  exact-active-class="active"
                >
                  <i :class="`bi ${child.icon} me-2`"></i>
                  <span>{{ child.label }}</span>
                </RouterLink>
              </div>
            </div>
          </template>
        </template>

        <div class="px-2 mt-3 mb-2 text-uppercase small text-muted fw-semibold">
          Switch
        </div>

        <RouterLink
          to="/me"
          class="nav-link"
          active-class="active"
          exact-active-class="active"
        >
          <i class="bi bi-person-circle me-2"></i>
          <span>Back to Member</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer mt-auto px-3 py-3 border-top">
        <div class="d-flex align-items-center gap-2">
          <div class="avatar">{{ initials }}</div>
          <div class="flex-grow-1">
            <div class="fw-semibold">{{ user?.name || "User" }}</div>
            <div class="text-muted small">{{ user?.role || "-" }}</div>
          </div>
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="doLogout"
            title="Logout"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </aside>

    <div class="main">
      <header class="topbar border-bottom bg-white">
        <div
          class="d-flex align-items-center justify-content-between px-3"
          style="height: 64px"
        >
          <div class="d-flex align-items-center gap-2 gap-md-3">
            <button
              class="btn btn-sm btn-outline-secondary d-inline-flex d-lg-none"
              @click="sidebarOpen = true"
              aria-label="Open menu"
            >
              <i class="bi bi-list"></i>
            </button>

            <div
              class="fw-semibold text-dark text-truncate"
              style="max-width: 52vw"
            >
              Admin: {{ user?.name || "..." }}
            </div>

            <span class="badge text-bg-light border">
              {{ user?.role || "admin" }}
            </span>
          </div>

          <div class="d-flex align-items-center gap-2">
            <div class="topbar-chip d-none d-md-inline-flex">
              <i class="bi bi-shield-lock me-1"></i> Admin Area
            </div>
          </div>
        </div>
      </header>

      <main class="content p-3 p-md-4">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { MENU_ADMIN } from "@/config/menu";
import { canAccess } from "@/utils/acl";
import { getUser, logout } from "@/services/auth";

export default {
  name: "AdminLayout",

  data() {
    return {
      user: getUser(),
      MENU: MENU_ADMIN,
      sidebarOpen: false,
      openGroups: {},
    };
  },

  computed: {
    filteredMenu() {
      const u = this.user;

      return this.MENU.filter((sec) => canAccess(u, sec.roles || []))
        .map((sec) => ({
          ...sec,
          items: (sec.items || [])
            .map((item) => {
              if (item.children && item.children.length) {
                const children = item.children.filter((child) =>
                  canAccess(u, child.roles || []),
                );

                return {
                  ...item,
                  children,
                };
              }

              return canAccess(u, item.roles || []) ? item : null;
            })
            .filter(Boolean)
            .filter((item) => {
              if (item.children) return item.children.length > 0;
              return true;
            }),
        }))
        .filter((sec) => sec.items.length > 0);
    },

    initials() {
      const n = this.user?.name || "";
      const parts = n.trim().split(" ").filter(Boolean);
      const a = parts[0]?.[0] || "U";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase();
    },
  },

  watch: {
    $route() {
      this.user = getUser();
      this.sidebarOpen = false;
      this.autoOpenActiveGroup();
    },
  },

  mounted() {
    this.user = getUser();
    this.autoOpenActiveGroup();
  },

  methods: {
    itemKey(item) {
      return item.to || item.label;
    },

    isOpen(key) {
      return !!this.openGroups[key];
    },

    toggleGroup(key) {
      this.openGroups = {
        ...this.openGroups,
        [key]: !this.openGroups[key],
      };
    },

    isParentActive(item) {
      if (!item.children || !item.children.length) return false;

      const currentPath = this.$route.path || "";
      return item.children.some((child) => currentPath.startsWith(child.to));
    },

    autoOpenActiveGroup() {
      const next = { ...this.openGroups };

      this.filteredMenu.forEach((sec) => {
        (sec.items || []).forEach((item) => {
          if (
            item.children &&
            item.children.length &&
            this.isParentActive(item)
          ) {
            next[this.itemKey(item)] = true;
          }
        });
      });

      this.openGroups = next;
    },

    async doLogout() {
      logout();
      this.$router.push("/login/sms");
    },
  },
};
</script>

<style scoped>
/* ===== Theme variables (easy to tweak) ===== */
.app-shell {
  --brand-1: #0d6efd;
  --brand-2: #6f42c1;
  --brand-3: #20c997;

  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: #f6f7fb;
}

/* overlay hidden by default */
.overlay {
  display: none;
}

/* ===== Sidebar (colorful) ===== */
.sidebar {
  display: flex;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.92);
  background: linear-gradient(160deg, var(--brand-2), var(--brand-1));
  border-right: 0 !important;
}

.sidebar-header {
  height: 72px;
}

/* brand block */
.brand-title {
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
}

.brand-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
}

.brand-icon {
  width: 50px; /* slightly bigger */
  height: 50px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.945);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #22c55e; /* finance green */
  font-size: 26px; /* larger icon */
}
.brand-icon i {
  font-size: 28px;
  color: #28a745;
}

/* section titles */
.sidebar .text-muted {
  color: rgba(255, 255, 255, 0.72) !important;
}

/* sidebar links */
.nav-link {
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.88);
  padding: 10px 12px;
  margin: 4px 6px;
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: background 0.15s ease, transform 0.15s ease;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transform: translateX(2px);
}

.nav-link.active {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-weight: 800;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
}

.nav-link-group {
  justify-content: space-between;
}

.nav-link-group.open {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.nav-group {
  display: block;
}

.submenu {
  margin-left: 10px;
  margin-bottom: 4px;
}

.nav-sublink {
  margin-left: 18px;
  padding-top: 8px;
  padding-bottom: 8px;
  font-size: 0.95rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
}

.nav-sublink.active {
  background: rgba(255, 255, 255, 0.2);
  font-weight: 700;
}

.submenu-chevron {
  font-size: 12px;
  opacity: 0.9;
}

/* footer */
.sidebar-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.16) !important;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-weight: 900;
}

/* buttons inside sidebar */
.sidebar .btn-outline-secondary {
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.28);
}

.sidebar .btn-outline-secondary:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.35);
}

/* ===== Main / Topbar (colorful) ===== */
.main {
  display: flex;
  flex-direction: column;
}

.topbar {
  border-bottom: 0 !important;
  background: linear-gradient(90deg, var(--brand-1), var(--brand-3)) !important;
  color: #fff;
}

.topbar .text-dark {
  color: #fff !important;
}

.topbar .badge.text-bg-light.border {
  background: rgba(255, 255, 255, 0.16) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
  color: #fff !important;
}

.topbar-chip {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 13px;
  color: #fff;
}

/* hamburger button in header */
.topbar .btn-outline-secondary {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.28);
}

.topbar .btn-outline-secondary:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.35);
}

.content {
  min-height: calc(100vh - 64px);
}

/* ===== Mobile behavior ===== */
@media (max-width: 991.98px) {
  .app-shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 240px;
    z-index: 1055;
    transform: translateX(-102%);
    transition: transform 0.2s ease;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  .app-shell.sidebar-open .sidebar {
    transform: translateX(0);
  }

  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1050;
    display: none;
  }

  .app-shell.sidebar-open .overlay {
    display: block;
  }
}
</style>
